import {
  AccordionItemProps,
  AccordionRootProps,
  useAccordionItemContext,
} from '@chakra-ui/react';
import {
  IQuoteConditionalTermSchema,
  QuoteConditionalTermStatusEnum,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { FunctionComponent as FC, useMemo, useState } from 'react';
import { BsArrowCounterclockwise } from 'react-icons/bs';
import { useGetAccountById } from '../../../../api/accountsService';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  doRevertQuoteConditionalTerm,
  doUpdateQuoteConditionalTerm,
} from '../../../../api/cpqService';
import { enqueueForQuote } from '../../../../api/quoteWriteChain';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MAccordionPanel,
  MAlert,
  MBox,
  MButton,
  MFlex,
  MIcon,
  MText,
  ResolvedTermsPreview,
  TemplateExpressionHelp,
  TermsPreviewToggle,
} from '../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../components/Monetize/ExpandCollapseButton';
import MEditor from '../../../../components/Monetize/MEditor';
import { TemplateExpressionWarning } from '../../../../components/Monetize/TemplateExpressionWarning';
import { useAccordionControl } from '../../../../hooks';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import { useTermsPreview } from '../../../../hooks/useTermsPreview';
import { useQuoteContext } from '../../Quote/quoteContext';

const getConditionalTerms = (
  conditionalTerms: Maybe<IQuoteConditionalTermSchema[]>,
) => {
  return (
    conditionalTerms?.filter(
      ({ status }) => status === QuoteConditionalTermStatusEnum.ACTIVE,
    ) || []
  );
};

type QuoteType = NonNullable<
  ReturnType<typeof useQuoteContext>['quoteData']['quote']
>;
type LoadingState = Record<string, { reverting?: boolean; editing?: boolean }>;

interface QuoteConditionalTermItemBodyProps {
  conditionalTerm: IQuoteConditionalTermSchema;
  index: number;
  isLastItem: boolean;
  editingIds: string[];
  loadingState: LoadingState;
  isReadOnly: boolean;
  quote: QuoteType;
  showResolvedPreview: boolean;
  previewError: unknown;
  resolvedTerms: string[] | null;
  isPreviewLoading: boolean;
  handleOnEditTerm: (
    conditionalTerm: IQuoteConditionalTermSchema,
    index: number,
  ) => void;
  handleOnRevertTerm: (
    conditionalTerm: IQuoteConditionalTermSchema,
    index: number,
  ) => void;
  handleOnChangeTerm: (
    value: string,
    conditionalTerm: IQuoteConditionalTermSchema,
  ) => void;
  handleOnSaveTerm: (
    conditionalTerm: IQuoteConditionalTermSchema,
  ) => Promise<void>;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const QuoteConditionalTermItemBody = ({
  conditionalTerm,
  index,
  isLastItem,
  editingIds,
  loadingState,
  isReadOnly,
  quote,
  showResolvedPreview,
  previewError,
  resolvedTerms,
  isPreviewLoading,
  handleOnEditTerm,
  handleOnRevertTerm,
  handleOnChangeTerm,
  handleOnSaveTerm,
}: QuoteConditionalTermItemBodyProps) => {
  const { expanded: isTermExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isTermExpanded}
        label={conditionalTerm.ruleName}
        labelProps={{ fontSize: 'sm' }}
        buttonProps={{ as: 'div', height: '40px', pr: 2 }}
      >
        {conditionalTerm.editableOnQuote &&
          !conditionalTerm.edited &&
          !editingIds.includes(conditionalTerm.id) && (
            <MButton
              isDisabled={isReadOnly}
              variant="tertiary"
              size="sm"
              onClick={(ev) => {
                ev.stopPropagation();
                handleOnEditTerm(conditionalTerm, index);
              }}
              isLoading={loadingState[conditionalTerm.id]?.editing}
            >
              Edit
            </MButton>
          )}
        {conditionalTerm.edited && (
          <MButton
            variant="tertiary"
            size="sm"
            onClick={(ev) => {
              ev.stopPropagation();
              handleOnRevertTerm(conditionalTerm, index);
            }}
            isLoading={loadingState[conditionalTerm.id]?.reverting}
          >
            <MIcon as={BsArrowCounterclockwise} mr="1" boxSize={4} />
            Revert to Original
          </MButton>
        )}
      </MAccordionCustomButton>

      <MAccordionPanel mt="2">
        {editingIds.includes(conditionalTerm.id) || conditionalTerm.edited ? (
          <>
            <MText color="tGray.acGray" fontStyle="italic" mb="1">
              Recommended to not edit Conditional Terms until Quote is
              substantially complete
            </MText>
            <MEditor
              autoScroll
              value={conditionalTerm.terms}
              handleEditorChange={(value) =>
                handleOnChangeTerm(value, conditionalTerm)
              }
              onBlur={() => handleOnSaveTerm(conditionalTerm)}
              boxProps={{
                pb: 0,
                minH: 150,
              }}
              disabled={isReadOnly || quote.status !== QuoteStatusEnum.DRAFT}
            />
            <TemplateExpressionHelp
              testId={`conditional-term-help-btn-${index}`}
            />
            <TemplateExpressionWarning value={conditionalTerm.terms} />
            {showResolvedPreview && !previewError && (
              <ResolvedTermsPreview
                label="Preview with quote data"
                html={resolvedTerms?.[index] ?? ''}
                isLoading={isPreviewLoading}
                testId={`terms-preview-content-${index}`}
                textColor="tPurple.base"
              />
            )}
          </>
        ) : (
          <MBox
            className="rich-text"
            color="tPurple.base"
            fontSize="sm"
            opacity={showResolvedPreview && isPreviewLoading ? 0.6 : 1}
            data-testid={`terms-preview-content-${index}`}
            dangerouslySetInnerHTML={{
              __html:
                showResolvedPreview && resolvedTerms?.[index] != null
                  ? resolvedTerms[index]
                  : conditionalTerm.terms,
            }}
          />
        )}
      </MAccordionPanel>
    </>
  );
};

interface QuoteConditionalTermsBodyProps {
  conditionalTerms: IQuoteConditionalTermSchema[];
  openValues: string[];
  setOpenValues: (values: string[]) => void;
  showResolvedPreview: boolean;
  setShowResolvedPreview: (value: boolean) => void;
  previewError: unknown;
  resolvedTerms: string[] | null;
  isPreviewLoading: boolean;
  editingIds: string[];
  loadingState: LoadingState;
  isReadOnly: boolean;
  quote: QuoteType;
  handleOnEditTerm: QuoteConditionalTermItemBodyProps['handleOnEditTerm'];
  handleOnRevertTerm: QuoteConditionalTermItemBodyProps['handleOnRevertTerm'];
  handleOnChangeTerm: QuoteConditionalTermItemBodyProps['handleOnChangeTerm'];
  handleOnSaveTerm: QuoteConditionalTermItemBodyProps['handleOnSaveTerm'];
}

const QuoteConditionalTermsBody = ({
  conditionalTerms,
  openValues,
  setOpenValues,
  showResolvedPreview,
  setShowResolvedPreview,
  previewError,
  resolvedTerms,
  isPreviewLoading,
  editingIds,
  loadingState,
  isReadOnly,
  quote,
  handleOnEditTerm,
  handleOnRevertTerm,
  handleOnChangeTerm,
  handleOnSaveTerm,
}: QuoteConditionalTermsBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label="Conditional Terms"
      />

      <MAccordionPanel m="0">
        <MFlex justifyContent="space-between" alignItems="center">
          <MBox>
            {conditionalTerms.length > 1 && (
              <ExpandCollapseButton
                isExpanded={openValues.length === conditionalTerms.length}
                onChange={(allExpanded) => {
                  allExpanded
                    ? setOpenValues(
                        conditionalTerms.map((_, index) => String(index)),
                      )
                    : setOpenValues([]);
                }}
              />
            )}
          </MBox>
          <TermsPreviewToggle
            isChecked={showResolvedPreview}
            onChange={setShowResolvedPreview}
            testId="terms-preview-toggle"
          />
        </MFlex>

        {showResolvedPreview && !!previewError && (
          <MAlert type="error" mb={2}>
            <MText>Could not generate the resolved preview.</MText>
          </MAlert>
        )}

        <MAccordion
          multiple
          value={openValues}
          onValueChange={({ value }) => setOpenValues(value)}
        >
          {conditionalTerms?.map((conditionalTerm, index) => (
            <MAccordionItem
              key={index}
              value={String(index)}
              borderRadius="none"
              borderColor="tBlue.hover"
              borderTop="none"
              borderLeft="none"
              borderRight="none"
              borderBottomWidth={
                index !== conditionalTerms.length - 1 ? '1px' : '0'
              }
            >
              <QuoteConditionalTermItemBody
                conditionalTerm={conditionalTerm}
                index={index}
                isLastItem={index === conditionalTerms.length - 1}
                editingIds={editingIds}
                loadingState={loadingState}
                isReadOnly={isReadOnly}
                quote={quote}
                showResolvedPreview={showResolvedPreview}
                previewError={previewError}
                resolvedTerms={resolvedTerms}
                isPreviewLoading={isPreviewLoading}
                handleOnEditTerm={handleOnEditTerm}
                handleOnRevertTerm={handleOnRevertTerm}
                handleOnChangeTerm={handleOnChangeTerm}
                handleOnSaveTerm={handleOnSaveTerm}
              />
            </MAccordionItem>
          ))}
        </MAccordion>
      </MAccordionPanel>
    </>
  );
};

export interface QuoteConditionalTermsProps extends AccordionRootProps {
  itemProps?: AccordionItemProps;
}

export const QuoteConditionalTerms: FC<QuoteConditionalTermsProps> = ({
  itemProps,
  ...rest
}: QuoteConditionalTermsProps) => {
  const {
    quoteData: { quote, fetchQuote },
    isReadOnly,
  } = useQuoteContext();

  const [editingIds, setEditingIds] = useState<string[]>([]);

  const [conditionalTerms, setConditionalTerms] = useState(() =>
    getConditionalTerms(quote?.conditionalTerms),
  );

  const [loadingState, setLoadingState] = useState<{
    [key: string]: { reverting?: boolean; editing?: boolean };
  }>({});

  // v3's Accordion.Root tracks expanded items by string `value`, not a
  // numeric index - each term's Accordion.Item `value` is its stringified
  // render position.
  const [openValues, setOpenValues] = useState<string[]>(
    quote?.conditionalTerms?.map((_, index) => String(index)) || [],
  );

  const { accordionValue, handleAccordionChange } = useAccordionControl({
    defaultIsExpanded: true,
  });

  useNonInitialEffect(() => {
    setConditionalTerms(getConditionalTerms(quote?.conditionalTerms));
  }, [quote?.conditionalTerms]);

  const [showResolvedPreview, setShowResolvedPreview] = useState(false);
  const { data: accountData, isLoading: isAccountLoading } = useGetAccountById(
    quote?.accountId ?? '',
    {
      enabled: showResolvedPreview && !!quote?.accountId,
      refetchOnWindowFocus: false,
    },
  );
  // Local state terms, so unsaved edits preview live
  const previewTermsInput = useMemo(
    () => conditionalTerms.map(({ terms }) => terms),
    [conditionalTerms],
  );
  const {
    resolvedTerms,
    isLoading: isPreviewLoading,
    error: previewError,
  } = useTermsPreview({
    quote,
    account: accountData,
    terms: previewTermsInput,
    enabled: showResolvedPreview && !!quote,
    isAccountLoading,
  });

  if (!quote || conditionalTerms?.length === 0) {
    return null;
  }

  const handleOnEditTerm = async (
    conditionalTerm: IQuoteConditionalTermSchema,
    index: number,
  ) => {
    if (!editingIds.includes(conditionalTerm.id)) {
      setEditingIds((ids) => [...ids, conditionalTerm.id]);
      try {
        setLoadingState({
          ...loadingState,
          [conditionalTerm.id]: {
            editing: true,
          },
        });
        await handleOnSaveTerm(conditionalTerm);
      } catch (err) {
        handleApiErrorToast(err);
      }

      if (!openValues.includes(String(index))) {
        setOpenValues([...openValues, String(index)]);
      }
    }

    setLoadingState({
      ...loadingState,
      [conditionalTerm.id]: {
        editing: false,
      },
    });
  };

  const handleOnChangeTerm = (
    value: string,
    conditionalTerm: IQuoteConditionalTermSchema,
  ) => {
    setConditionalTerms((terms) =>
      terms.map((term) =>
        term.id === conditionalTerm.id ? { ...term, terms: value } : term,
      ),
    );
  };
  const handleOnSaveTerm = async (
    conditionalTerm: IQuoteConditionalTermSchema,
  ) => {
    if (!quote) {
      return;
    }
    try {
      const newCondTerm = await enqueueForQuote(quote.id, () =>
        doUpdateQuoteConditionalTerm(quote.id, conditionalTerm.id, {
          terms: conditionalTerm.terms,
        }),
      );
      setConditionalTerms((terms) =>
        terms.map((term) =>
          term.id === conditionalTerm.id ? newCondTerm : term,
        ),
      );
      setEditingIds((ids) => ids.filter((id) => id !== conditionalTerm.id));
      await fetchQuote(quote.id);
    } catch (err) {
      handleApiErrorToast(err);
    }
  };
  const handleOnRevertTerm = async (
    conditionalTerm: IQuoteConditionalTermSchema,
    index: number,
  ) => {
    if (!quote) {
      return;
    }
    if (!openValues.includes(String(index))) {
      setOpenValues([...openValues, String(index)]);
    }

    try {
      setLoadingState({
        ...loadingState,
        [conditionalTerm.id]: {
          reverting: true,
        },
      });
      const newCondTerm = await enqueueForQuote(quote.id, () =>
        doRevertQuoteConditionalTerm(quote.id, conditionalTerm.id),
      );
      setConditionalTerms((terms) =>
        terms.map((term) =>
          term.id === conditionalTerm.id ? newCondTerm : term,
        ),
      );
      setEditingIds((ids) => ids.filter((id) => id !== conditionalTerm.id));
      await fetchQuote(quote.id);
    } catch (err) {
      handleApiErrorToast(err);
    }
    setLoadingState({
      ...loadingState,
      [conditionalTerm.id]: {
        reverting: false,
      },
    });
  };

  return (
    <MAccordion
      multiple
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
      {...rest}
    >
      <MAccordionItem value="0" borderColor="tGray.lightGray">
        <QuoteConditionalTermsBody
          conditionalTerms={conditionalTerms}
          openValues={openValues}
          setOpenValues={setOpenValues}
          showResolvedPreview={showResolvedPreview}
          setShowResolvedPreview={setShowResolvedPreview}
          previewError={previewError}
          resolvedTerms={resolvedTerms}
          isPreviewLoading={isPreviewLoading}
          editingIds={editingIds}
          loadingState={loadingState}
          isReadOnly={isReadOnly}
          quote={quote}
          handleOnEditTerm={handleOnEditTerm}
          handleOnRevertTerm={handleOnRevertTerm}
          handleOnChangeTerm={handleOnChangeTerm}
          handleOnSaveTerm={handleOnSaveTerm}
        />
      </MAccordionItem>
    </MAccordion>
  );
};
